import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Fab,
  Fade,
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useI18n } from '../hooks/useI18n';

const PDFIframeViewer: React.FC = () => {
  const [scale, setScale] = useState(1.0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useI18n();

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = '/dic/tu-dien-lao-viet.pdf';
    link.download = 'tu-dien-lao-viet.pdf';
    link.click();
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
          : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        p: { xs: 2, sm: 3 },
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 3,
          p: 3,
          color: 'white',
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {t('dictionary.title')}
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          {t('dictionary.subtitle')}
        </Typography>
      </Box>

      {/* PDF Controls */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {/* Zoom Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={t('dictionary.zoom_out')}>
            <IconButton
              onClick={handleZoomOut}
              disabled={scale <= 0.5}
              sx={{
                background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                },
              }}
            >
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
          
          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '60px', textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </Typography>
          
          <Tooltip title={t('dictionary.zoom_in')}>
            <IconButton
              onClick={handleZoomIn}
              disabled={scale >= 3.0}
              sx={{
                background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                },
              }}
            >
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={t('dictionary.download')}>
            <IconButton
              onClick={handleDownload}
              sx={{
                background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                },
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={isFullscreen ? t('dictionary.exit_fullscreen') : t('dictionary.fullscreen')}>
            <IconButton
              onClick={toggleFullscreen}
              sx={{
                background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                },
              }}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* PDF Content */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          minHeight: '70vh',
          background: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
        }}
      >
        <iframe
          src="/dic/tu-dien-lao-viet.pdf"
          style={{
            width: '100%',
            height: '70vh',
            border: 'none',
            transform: `scale(${scale})`,
            transformOrigin: 'top center',
          }}
          title="Từ điển Lào - Việt"
        />
      </Box>

      {/* Floating Action Button for Mobile */}
      {isMobile && (
        <Fade in={true}>
          <Fab
            color="primary"
            aria-label="Trang chủ"
            onClick={() => window.history.back()}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
            }}
          >
            <HomeIcon />
          </Fab>
        </Fade>
      )}

      {/* Instructions */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          💡 {t('dictionary.tips')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('dictionary.description')}
        </Typography>
      </Box>
    </Box>
  );
};

export default PDFIframeViewer; 